import React from 'react';
import { Box, Flex, FormControl, FormLabel, Select, SimpleGrid, Text } from '@chakra-ui/react';
import { Sparkle } from 'phosphor-react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { CurrencyInputForm } from '../../../../../../components/inputs/forms/CurrencyInputForm';
import { toNumber } from '../../../../../../utils/FormatFunctions';
import { NfseDeducaoTipo } from '../../../../../../types/nfseEmissao';
import { TRIB_ISSQN_OPCOES } from '../../shared/constants';
import { brl, C, NButton, Segmented, StatusTag } from '../../shared/ui';
import { FormSection } from './FormSection';

const RETENCOES = [
  { name: 'retencoes.irrf', label: 'IRRF' },
  { name: 'retencoes.csll', label: 'CSLL' },
  { name: 'retencoes.pis', label: 'PIS' },
  { name: 'retencoes.cofins', label: 'COFINS' },
  { name: 'retencoes.cp', label: 'CP' },
];

const RETENCAO_ISS = [
  { value: '1', label: 'Não retido' },
  { value: '2', label: 'Retido pelo tomador' },
  { value: '3', label: 'Pelo intermediário' },
];

const DEDUCAO_TIPOS: { value: NfseDeducaoTipo; label: string }[] = [
  { value: 'valor', label: 'R$' },
  { value: 'percentual', label: '%' },
];

// IRRF 1,5% e CSRF 4,65% (CSLL 1%, PIS 0,65%, COFINS 3%): sugestão, a pessoa confere.
const SUGESTAO = { irrf: 0.015, csll: 0.01, pis: 0.0065, cofins: 0.03 };
const centavos = (v: number) => Math.round(v * 100) / 100;

export function ValoresSection({
  isDisabled,
  editado,
  municipio,
}: {
  isDisabled?: boolean;
  editado: (campo: string) => boolean;
  municipio?: string | null;
}) {
  const { register, control, setValue } = useFormContext();
  const vServ = toNumber(useWatch({ name: 'v_serv' }));
  const deducaoTipo = ((useWatch({ name: 'deducao_tipo' }) as NfseDeducaoTipo) ||
    'valor') as NfseDeducaoTipo;
  const deducao = toNumber(useWatch({ name: 'deducao' }));
  const pAliq = toNumber(useWatch({ name: 'p_aliq' }));
  // RN18: base = valor do serviço − dedução; ISS = base × alíquota. Sem alíquota informada
  // a Sefin aplica a do município, então a tela só mostra a base.
  const valorDeduzido = centavos(deducaoTipo === 'percentual' ? (vServ * deducao) / 100 : deducao);
  const base = centavos(vServ - valorDeduzido);

  const sugerir = () => {
    Object.entries(SUGESTAO).forEach(([campo, aliquota]) =>
      setValue(`retencoes.${campo}`, centavos(vServ * aliquota), { shouldDirty: true }),
    );
  };

  return (
    <FormSection
      n={4}
      title="Valores e tributos"
      subtitle="Alíquota do ISS e tributos aproximados vêm do padrão do emitente"
    >
      <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} columnGap={4} rowGap={4}>
        <CurrencyInputForm
          name="v_serv"
          label="Valor do serviço"
          isRequired
          controlMb={0}
          isDisabled={isDisabled}
        />
        <FormControl>
          <FormLabel fontWeight="bold" mb={1} htmlFor="trib_issqn">
            Tributação do ISS
          </FormLabel>
          <Select id="trib_issqn" isDisabled={isDisabled} {...register('trib_issqn')}>
            {TRIB_ISSQN_OPCOES.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          {editado('trib_issqn') && (
            <Text fontSize="12px" color={C.brand700} mt="6px">
              editado
            </Text>
          )}
        </FormControl>
        <Box>
          <CurrencyInputForm
            name="p_aliq"
            label="Alíquota do ISS (%)"
            prefix=""
            controlMb={0}
            isDisabled={isDisabled}
          />
          <Text fontSize="12px" color={editado('p_aliq') ? C.brand700 : C.subtle} mt="6px">
            {editado('p_aliq') ? 'editado' : `Parâmetro de ${municipio ?? 'município'}`}
          </Text>
        </Box>
        <Box>
          <CurrencyInputForm
            name="p_tot_trib_fed"
            label="Tributos federais aprox. (%)"
            prefix=""
            controlMb={0}
            isDisabled={isDisabled}
          />
          <Text fontSize="12px" color={editado('p_tot_trib_fed') ? C.brand700 : C.subtle} mt="6px">
            {editado('p_tot_trib_fed') ? 'editado' : 'pTotTribFed'}
          </Text>
        </Box>
      </SimpleGrid>

      <Flex mt={5} align="center" gap="14px" wrap="wrap">
        <Text fontSize="12px" fontWeight={600} color={C.muted}>
          Retenção do ISS
        </Text>
        <Controller
          name="tp_ret_issqn"
          control={control}
          render={({ field }) => (
            <Segmented
              id="ret-iss"
              label="Retenção do ISS"
              value={String(field.value ?? '1')}
              onChange={field.onChange}
              options={RETENCAO_ISS}
              isDisabled={isDisabled}
            />
          )}
        />
        {editado('tp_ret_issqn') && <StatusTag kind="enviando">editado</StatusTag>}
      </Flex>

      <Flex mt={5} align="flex-end" gap="14px" wrap="wrap">
        <Box>
          <Text fontSize="12px" fontWeight={600} color={C.muted} mb={2}>
            Dedução da base do ISS
          </Text>
          <Controller
            name="deducao_tipo"
            control={control}
            render={({ field }) => (
              <Segmented
                id="deducao-tipo"
                label="Dedução em reais ou percentual"
                value={deducaoTipo}
                onChange={v => {
                  field.onChange(v);
                  setValue('deducao', '', { shouldDirty: true, shouldValidate: true });
                }}
                options={DEDUCAO_TIPOS}
                isDisabled={isDisabled}
              />
            )}
          />
        </Box>
        <Box w={{ base: 'full', md: '200px' }}>
          <CurrencyInputForm
            name="deducao"
            label={deducaoTipo === 'percentual' ? 'Dedução (%)' : 'Dedução (R$)'}
            prefix={deducaoTipo === 'percentual' ? '' : 'R$ '}
            placeholder="Opcional"
            controlMb={0}
            isDisabled={isDisabled}
          />
        </Box>
        {deducao > 0 && vServ > 0 && (
          <Text fontSize="13px" color={C.muted} pb="10px">
            Base de cálculo <b>{brl(base)}</b>
            {pAliq > 0
              ? ` · ISS ${brl(centavos((base * pAliq) / 100))}`
              : ' · ISS pela alíquota do município'}
          </Text>
        )}
      </Flex>

      <Box h="1px" bg={C.line} my={5} />
      <Flex align="center" justify="space-between" gap={3} mb={3} wrap="wrap">
        <Text as="h3" fontSize="13px" fontWeight={700} color={C.muted}>
          Retenções federais (R$)
        </Text>
        <NButton
          variant="ghost"
          size="sm"
          icon={Sparkle}
          isDisabled={isDisabled || !vServ}
          onClick={sugerir}
        >
          Sugerir retenções (IRRF 1,5% · CSRF 4,65%)
        </NButton>
      </Flex>
      <SimpleGrid columns={{ base: 2, md: 5 }} columnGap={4}>
        {RETENCOES.map(r => (
          <CurrencyInputForm
            key={r.name}
            name={r.name}
            label={r.label}
            controlMb={0}
            isDisabled={isDisabled}
          />
        ))}
      </SimpleGrid>
    </FormSection>
  );
}
